import { useParams, useNavigate } from 'react-router-dom'
import { useDelivery, usePickupOrder, useDeliverOrder } from '@/hooks/useDeliveries'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { formatCurrency, formatTime, openInMaps } from '@/lib/utils'
import { 
  ArrowLeft, 
  MapPin, 
  Phone, 
  Clock, 
  Package,
  Navigation,
  CheckCircle,
  Loader2
} from 'lucide-react'

export default function DeliveryDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const delivery = useDelivery(orderId!)
  const pickupMutation = usePickupOrder()
  const deliverMutation = useDeliverOrder()

  if (!delivery) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const handlePickup = () => {
    pickupMutation.mutate({ orderId: delivery.orderId })
  }

  const handleDeliver = () => {
    deliverMutation.mutate({ orderId: delivery.orderId })
  }

  const canPickup = ['assigned', 'pickup_ready'].includes(delivery.status)
  const canDeliver = ['picked_up', 'in_transit'].includes(delivery.status)
  const isCompleted = delivery.status === 'delivered'

  const pickupAddress = `${delivery.pickup.address}, ${delivery.pickup.postalCode} ${delivery.pickup.city}`
  const dropoffAddress = `${delivery.dropoff.address}, ${delivery.dropoff.postalCode} ${delivery.dropoff.city}`

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold">Order #{delivery.orderNumber}</h1>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(delivery.deliveryFee)} delivery fee
          </p>
        </div>
      </div>

      {/* Status Timeline */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <Step 
              label="Assigned" 
              completed={true}
              active={delivery.status === 'assigned'}
            />
            <div className="flex-1 h-0.5 bg-muted mx-2" />
            <Step 
              label="Ready" 
              completed={['pickup_ready', 'picked_up', 'in_transit', 'delivered'].includes(delivery.status)}
              active={delivery.status === 'pickup_ready'}
            />
            <div className="flex-1 h-0.5 bg-muted mx-2" />
            <Step 
              label="Picked Up" 
              completed={['picked_up', 'in_transit', 'delivered'].includes(delivery.status)}
              active={delivery.status === 'picked_up'}
            />
            <div className="flex-1 h-0.5 bg-muted mx-2" />
            <Step 
              label="Delivered" 
              completed={delivery.status === 'delivered'}
              active={delivery.status === 'delivered'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pickup Location */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="w-4 h-4 text-primary" />
            Pickup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="font-medium">{delivery.pickup.chefName}</p>
            <p className="text-sm text-muted-foreground">{pickupAddress}</p>
          </div>
          {delivery.pickup.readyAt && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>Ready at {formatTime(delivery.pickup.readyAt)}</span>
            </div>
          )}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => openInMaps(pickupAddress, delivery.pickup.latitude, delivery.pickup.longitude)}
          >
            <Navigation className="w-4 h-4 mr-2" />
            Navigate to Pickup
          </Button>
        </CardContent>
      </Card>

      {/* Dropoff Location */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4 text-secondary" />
            Dropoff
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <p className="font-medium">{delivery.customerName}</p>
            <p className="text-sm text-muted-foreground">{dropoffAddress}</p>
          </div>
          {delivery.customerPhone && (
            <a 
              href={`tel:${delivery.customerPhone}`}
              className="flex items-center gap-2 text-sm text-primary"
            >
              <Phone className="w-4 h-4" />
              <span>{delivery.customerPhone}</span>
            </a>
          )}
          {delivery.dropoff.deliveryInstructions && (
            <div className="p-3 bg-muted rounded-lg text-sm">
              <p className="font-medium mb-1">Delivery Instructions:</p>
              <p>{delivery.dropoff.deliveryInstructions}</p>
            </div>
          )}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => openInMaps(dropoffAddress, delivery.dropoff.latitude, delivery.dropoff.longitude)}
          >
            <Navigation className="w-4 h-4 mr-2" />
            Navigate to Dropoff
          </Button>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {!isCompleted && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
          {canPickup && (
            <Button 
              size="xl" 
              className="w-full"
              onClick={handlePickup}
              disabled={pickupMutation.isPending}
            >
              {pickupMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Package className="w-5 h-5 mr-2" />
              )}
              Mark as Picked Up
            </Button>
          )}
          {canDeliver && (
            <Button 
              size="xl" 
              variant="success"
              className="w-full"
              onClick={handleDeliver}
              disabled={deliverMutation.isPending}
            >
              {deliverMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              Mark as Delivered
            </Button>
          )}
        </div>
      )}

      {/* Completed Badge */}
      {isCompleted && (
        <Card className="bg-success/10 border-success">
          <CardContent className="py-6 text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-success" />
            <p className="font-semibold text-success">Delivery Completed</p>
            <p className="text-sm text-muted-foreground">
              Delivered at {delivery.deliveredAt ? formatTime(delivery.deliveredAt) : 'N/A'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Spacer for fixed button */}
      {!isCompleted && <div className="h-20" />}
    </div>
  )
}

function Step({ label, completed, active }: { label: string; completed: boolean; active: boolean }) {
  return (
    <div className="flex flex-col items-center">
      <div 
        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
          ${completed ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}
        `}
      >
        {completed ? '✓' : ''}
      </div>
      <span className="text-xs mt-1 text-muted-foreground">{label}</span>
    </div>
  )
}
